import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Users } from 'lucide-react';
import { trackGarageDiscoveryImpression, trackGarageDiscoveryClick } from '@/lib/analytics';

interface CommunityGarageCtaProps {
  position: number;
  source: 'feed_list' | 'feed_grid';
}

// UI-097: Community CTA card injected every 15 feed items to surface garage
// discovery. Drives Community lens from 🔴 → 🟡 by boosting awareness of
// the garage feature among feed-scrolling users.
export function CommunityGarageCTA({ position, source }: CommunityGarageCtaProps) {
  const impressionFiredRef = useRef(false);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !impressionFiredRef.current) {
          impressionFiredRef.current = true;
          trackGarageDiscoveryImpression(position, source);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [position, source]);

  return (
    <div ref={ctaRef} className="rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-violet-600/5 px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-500/15 shrink-0">
            <Users className="h-4 w-4 text-violet-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-text-primary">Join a Garage</p>
            <p className="text-xs text-text-tertiary">
              Connect with enthusiasts who share your passion. Save articles, discuss launches.
            </p>
          </div>
        </div>
        <Link
          to="/garages"
          onClick={() => trackGarageDiscoveryClick(position, source)}
          className="inline-flex items-center justify-center rounded-xl bg-violet-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-600 whitespace-nowrap"
        >
          Browse Garages
        </Link>
      </div>
    </div>
  );
}
